import PropTypes from 'prop-types';
import {Component} from 'react';
import Language from './language';

/**
 * Provide a translation in-place
 *
 * @param children: string
 * The key to use for the translation
 * @returns React node: the translated, formatted text
 *
 * Example usage
 * <Translation>btn.submit</Translation>
 *
 * Translation Markup
 * Work in progress: currently only supports line breaks.
 */

const elementMap = {
  b: 'strong',
  n: 'br',
  l: 'a',
  t: 'a',
  p: 'a',
  r: 'a',
  a: 'a',
  c: 'span',
  s: 'span',
};

const tags = Object.keys(elementMap).join(''); // Joins all elementMap key into a string
const tagOnlyRegex = new RegExp(`\\[[${tags}]/?]`); // Any character that matches the char in tag strings
const tagRegex = new RegExp(`^(.*?)\\[([${tags}])(\\/?)](.*)$`);
const placeholderRegex = new RegExp('(.*?){(\\d+)}(.*)'); // Find placeholders eg {0}

class Translation extends Component {
  static getText(key, placeholderValues) {
    return Language.getText(key, placeholderValues);
  }
  
  getTagProps(tag) {
    switch (tag) {
      case 'c': {
        if (this.props.tagProps) return {...this.props.tagProps.shift(), className: 'chineseTranslations'};
        return {className: 'chineseTranslations'};
      }
      case 't':
      case 'p':
      case 'r':
      case 'a': {
        return this.props.tagProps && this.retrieveAndRemoveSpecificTagProp(tag);
      }
      default:
        return this.props.tagProps && this.props.tagProps.shift();
    }
  }
  
  retrieveAndRemoveSpecificTagProp(tag) {
    return this.props.tagProps.find((tagProp) => {
      if (tagProp['data-tag-type'] === tag) {
        this.props.tagProps.splice(this.props.tagProps.indexOf(tagProp), 1);
      }
      return tagProp['data-tag-type'] === tag;
    });
  }
  
  processPlaceholders(text) {
    if (!this.props.placeholderValues || !this.props.placeholderValues.length) return text;
    
    const matches = text.match(placeholderRegex);
    
    if (!matches) return text;
    
    return (
      <>
        {matches[1]}
        {this.props.placeholderValues[Number(matches[2])]}
        {this.processPlaceholders(matches[3])}
      </>
    );
  }
  
  findMarkup(markupString) {
    // Find tag and break into parts
    const matches = markupString.match(tagRegex);
    
    // Can't find tag that matches expected format
    if (!matches || matches.length !== 5) return false;
    
    return {
      before: matches[1],
      tag: matches[2],
      after: matches[4],
      isSelfClosing: matches[3] === '/',
    };
  }
  
  processMarkup(markupString) {
    // This is for performance - short circuit if null input or no tag is found
    if (!markupString || !tagOnlyRegex.test(markupString)) return this.processPlaceholders(markupString);
    
    const markup = this.findMarkup(markupString);
    if (!markup) return this.processPlaceholders(markupString);
    
    const domElement = elementMap[markup.tag];
    const tagProps = this.getTagProps(markup.tag);
    
    // eg new line [n/] Render without children. Don't need to find closing tag
    if (markup.isSelfClosing) return this.renderMarkup(domElement, tagProps, markup.before, null, markup.after);
    
    const closingTagMatches = markup.after.match(`^(.*?)\\[\\/(${markup.tag})](.*)$`);
    
    // Can't find closing tag, remove the tag
    if (!closingTagMatches || closingTagMatches.length !== 4) return markup.before + markup.after;
    
    const tagContents = closingTagMatches[1];
    const afterClosingTag = closingTagMatches[3];
    
    return this.renderMarkup(domElement, tagProps, markup.before, tagContents, afterClosingTag);
  }
  
  renderMarkup(Element, props, before, children, after) {
    return (
      <>
        {before}
        <Element {...props}>
          {this.processMarkup(children)}
        </Element>
        {this.processMarkup(after)}
      </>
    );
  }
  
  render() {
    const getKeyToUse = () => {
      if (!this.props.optionalExtension) {
        return this.props.children;
      }
      const translationWithOptionalExtension = `${this.props.children}.${this.props.optionalExtension}`;
      return Language.doesTranslationExist(translationWithOptionalExtension) ? translationWithOptionalExtension : this.props.children;
    };
    
    const translatedText = Translation.getText(getKeyToUse());
    if (!translatedText) return '';
    
    // span here is to protect from Google Translate assassinating React virtual DOM
    // SEE https://bugs.chromium.org/p/chromium/issues/detail?id=872770
    return <span className="Translation">{this.processMarkup(translatedText)}</span>;
  }
}

Translation.propTypes = {
  children: PropTypes.string.isRequired,
  tagProps: PropTypes.arrayOf(PropTypes.object),
  placeholderValues: PropTypes.arrayOf(PropTypes.node),
  optionalExtension: PropTypes.string,
};

Translation.defaultProps = {
  placeholderValues: [],
  optionalExtension: '',
};

Translation.defaultProps = {
  tagProps: [],
};

export default Translation;
